import React, { useEffect, useState } from "react";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { TrendUp, Clock, Percent, Receipt } from "@phosphor-icons/react";

const monthLabel = (m) => { const [y, mm] = m.split("-"); return `${["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"][parseInt(mm) - 1]}/${y.slice(2)}`; };

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="card-crisp p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</span><Icon size={18} weight="duotone" className="text-indigo-600" /></div>
      <div className="font-mono-nums text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Panel({ title, subtitle, children, testId }) {
  return (
    <div className="card-crisp p-6" data-testid={testId}>
      <h3 className="font-heading font-semibold text-lg text-slate-900">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function CommissionsPanel() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [blocked, setBlocked] = useState(false);
  useEffect(() => { api.get(`/reports/commissions${month ? `?month=${month}` : ""}`).then((r) => { setData(r.data); if (!month) setMonth(r.data.month); }).catch((e) => { if (e.response?.status === 402) setBlocked(true); }); }, [month]);
  if (blocked) return <Panel title="Comissionamento por profissional" testId="rep-commissions"><p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">Disponível a partir do plano Master.</p></Panel>;
  if (!data) return null;
  return (
    <Panel title="Comissionamento por profissional" subtitle="Orçamentos aprovados no mês × comissão de cada profissional." testId="rep-commissions">
      <select value={month} onChange={(e) => setMonth(e.target.value)} className="text-sm border border-slate-200 rounded-md px-2 py-1 mb-3" data-testid="rep-commissions-month">{data.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}</select>
      <table className="w-full text-sm">
        <thead className="text-left text-slate-500 border-b border-slate-200"><tr><th className="py-2 font-medium">Profissional</th><th className="py-2 font-medium text-right">Aprovado</th><th className="py-2 font-medium text-right">Comissão</th></tr></thead>
        <tbody>
          {data.rows.map((row) => <tr key={row.professional_id} className="border-b border-slate-100"><td className="py-2"><div className="text-slate-900">{row.name}</div><div className="text-xs text-slate-500">{row.quotes} orçamento(s) · {row.commission_pct}%</div></td><td className="py-2 text-right font-mono-nums">{formatCurrency(row.revenue)}</td><td className="py-2 text-right font-mono-nums font-semibold text-indigo-700">{formatCurrency(row.commission)}</td></tr>)}
          {!data.rows.length && <tr><td colSpan={3} className="py-6 text-center text-slate-500">Nenhum orçamento aprovado neste mês.</td></tr>}
        </tbody>
        {data.rows.length > 0 && <tfoot><tr className="font-semibold"><td className="py-2">Total</td><td className="py-2 text-right font-mono-nums">{formatCurrency(data.total_revenue)}</td><td className="py-2 text-right font-mono-nums text-indigo-700">{formatCurrency(data.total_commission)}</td></tr></tfoot>}
      </table>
    </Panel>
  );
}

export default function ReportsPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!data) return <div className="text-slate-500">Carregando relatórios...</div>;
  const { summary, ranking, hour_history, quotes_history, most_quoted, categories, funnel, at_risk } = data;
  const top = ranking.slice(0, 10);
  const bottom = [...ranking].reverse().slice(0, 5);

  return (
    <div data-testid="reports-page">
      <PageHeader title="Relatórios de lucratividade" subtitle="Quais procedimentos rendem mais por hora de cadeira e como sua hora clínica evolui." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Clock} label="Hora clínica atual" value={formatCurrency(summary.clinical_hour_cost)} testId="rep-hour" />
        <Stat icon={Percent} label="Margem média" value={`${summary.avg_margin}%`} testId="rep-margin" />
        <Stat icon={Receipt} label="Orçamentos gerados" value={summary.quotes_count} testId="rep-quotes" />
        <Stat icon={TrendUp} label="Total orçado" value={formatCurrency(summary.quotes_total)} testId="rep-total" />
      </div>

      {funnel && (
        <div className="grid sm:grid-cols-4 gap-4 mb-6" data-testid="rep-funnel">
          <Stat icon={Receipt} label="Pendentes" value={funnel.pending} testId="rep-funnel-pending" />
          <Stat icon={TrendUp} label="Aprovados" value={`${funnel.approved} · ${formatCurrency(funnel.approved_total)}`} testId="rep-funnel-approved" />
          <Stat icon={Receipt} label="Perdidos" value={funnel.lost} testId="rep-funnel-lost" />
          <Stat icon={Percent} label="Taxa de conversão" value={funnel.conversion_pct === null ? "—" : `${funnel.conversion_pct}%`} testId="rep-funnel-conversion" />
        </div>
      )}
      {at_risk?.length > 0 && (
        <Panel title="Procedimentos com margem apertada ou em risco" subtitle="Comparando o preço que você cobra hoje com o mínimo e o ideal." testId="rep-at-risk">
          <ul className="divide-y divide-slate-100 text-sm">
            {at_risk.map((p) => <li key={p.id} className="py-2 flex justify-between gap-3"><span className="text-slate-800">{p.name}</span><span className={`font-mono-nums ${p.status === "risco" ? "text-rose-600" : "text-amber-700"}`}>cobra {formatCurrency(p.current_price)} · margem {p.current_margin_pct}%</span></li>)}
          </ul>
        </Panel>
      )}
      <div className="grid lg:grid-cols-2 gap-6 mb-6 mt-6">
        <Panel title="Evolução da hora clínica" subtitle="Registrada automaticamente a cada mês em que você usa o sistema." testId="rep-hour-chart">
          {hour_history.length < 2 ? (
            <div className="h-56 flex items-center justify-center text-sm text-slate-500 text-center">Ainda temos só {hour_history.length} mês registrado ({hour_history[0] ? formatCurrency(hour_history[0].clinical_hour_cost) : ""}). O gráfico aparece a partir do 2º mês.</div>
          ) : (
            <ResponsiveContainer width="100%" height={224}>
              <LineChart data={hour_history.map((h) => ({ ...h, label: monthLabel(h.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `R$${v}`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Line type="monotone" dataKey="clinical_hour_cost" name="Hora clínica" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Orçamentos por mês" subtitle="Valor total orçado nos últimos meses." testId="rep-quotes-chart">
          {quotes_history.length === 0 ? <div className="h-56 flex items-center justify-center text-sm text-slate-500">Nenhum orçamento ainda.</div> : (
            <ResponsiveContainer width="100%" height={224}>
              <BarChart data={quotes_history.map((h) => ({ ...h, label: monthLabel(h.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `R$${v}`} />
                <Tooltip formatter={(v, n) => (n === "Total" ? formatCurrency(v) : v)} />
                <Bar dataKey="total" name="Total" fill="#4f46e5" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Panel title="Ranking de lucratividade" subtitle="Ordenado por lucro por hora de cadeira (preço ideal − custo, ÷ duração). É isso que mostra onde vale mais a pena investir sua agenda." testId="rep-ranking">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500 border-b border-slate-200"><tr><th className="py-2 font-medium">#</th><th className="py-2 font-medium">Procedimento</th><th className="py-2 font-medium text-right">Preço ideal</th><th className="py-2 font-medium text-right">Lucro</th><th className="py-2 font-medium text-right">Margem</th><th className="py-2 font-medium text-right">Lucro/hora</th></tr></thead>
                <tbody>
                  {top.map((r, i) => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`rep-rank-${i}`}>
                      <td className="py-2.5 text-slate-400 font-mono-nums">{i + 1}</td>
                      <td className="py-2.5"><div className="font-medium text-slate-900">{r.name}</div><div className="text-xs text-slate-500">{r.category} · {r.duration_minutes} min</div></td>
                      <td className="py-2.5 text-right font-mono-nums">{formatCurrency(r.price_ideal)}</td>
                      <td className="py-2.5 text-right font-mono-nums text-emerald-700">{formatCurrency(r.profit)}</td>
                      <td className="py-2.5 text-right font-mono-nums">{r.margin_pct}%</td>
                      <td className="py-2.5 text-right font-mono-nums font-bold text-indigo-700">{formatCurrency(r.profit_per_hour)}</td>
                    </tr>
                  ))}
                  {!ranking.length && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Cadastre procedimentos para ver o ranking.</td></tr>}
                </tbody>
              </table>
            </div>
            {bottom.length > 0 && ranking.length > 10 && (
              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Menor lucro por hora</div>
                <ul className="text-sm space-y-1">{bottom.map((r) => <li key={r.id} className="flex justify-between"><span className="text-slate-700">{r.name}</span><span className="font-mono-nums text-rose-600">{formatCurrency(r.profit_per_hour)}/h</span></li>)}</ul>
              </div>
            )}
          </Panel>
        </div>
        <div className="space-y-6">
          <Panel title="Mais orçados" subtitle="Procedimentos que mais aparecem nos orçamentos." testId="rep-most-quoted">
            {most_quoted.length === 0 ? <p className="text-sm text-slate-500">Sem dados ainda.</p> : (
              <ul className="space-y-2 text-sm">{most_quoted.map((m) => <li key={m.name} className="flex justify-between"><span className="text-slate-700 truncate pr-2">{m.name}</span><span className="font-mono-nums font-semibold">{m.quantity}x</span></li>)}</ul>
            )}
          </Panel>
          <CommissionsPanel />
          <Panel title="Margem por especialidade" testId="rep-categories">
            <ul className="space-y-2 text-sm">{categories.map((c) => <li key={c.category} className="flex justify-between"><span className="text-slate-700">{c.category} <span className="text-slate-400">({c.count})</span></span><span className="font-mono-nums font-semibold">{c.avg_margin}%</span></li>)}</ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}
